import { addYears, isAfter } from 'date-fns';
import { safelyParseDate } from '@/utils/date';

export const INFORMED_CONSENTS_COLLECTION = 'informedConsents';
export const INFORMED_CONSENT_VALIDITY_YEARS = 1;

export const CONSENT_STATUS = {
	NONE: 'none',
	RECEIVED: 'received',
	EXPIRED: 'expired',
	NOT_APPLICABLE: 'notApplicable',
};

export const CONSENT_STATUS_OPTIONS = [
	{ value: CONSENT_STATUS.RECEIVED, label: 'Received' },
	{ value: CONSENT_STATUS.EXPIRED, label: 'Expired' },
	{ value: CONSENT_STATUS.NOT_APPLICABLE, label: 'Not Applicable' },
	{ value: CONSENT_STATUS.NONE, label: 'None' },
];

export const getConsentStatus = (dateReceived, now = new Date(), notApplicable = false) => {
	if (notApplicable) {
		return CONSENT_STATUS.NOT_APPLICABLE;
	}

	const receivedDate = safelyParseDate(dateReceived);
	if (!receivedDate) {
		return CONSENT_STATUS.NONE;
	}

	const expiresAt = addYears(receivedDate, INFORMED_CONSENT_VALIDITY_YEARS);
	return isAfter(now, expiresAt) ? CONSENT_STATUS.EXPIRED : CONSENT_STATUS.RECEIVED;
};

// The stored status on a client/deal only flips from received to expired at the nightly
// sync, so a received status whose expiry has passed is treated as expired straight away.
export const getRowConsentStatus = (row, now = new Date()) =>
	row.informedConsentStatus === CONSENT_STATUS.RECEIVED &&
	isAfter(now, safelyParseDate(row.informedConsentDateExpiry))
		? CONSENT_STATUS.EXPIRED
		: row.informedConsentStatus;

// Typesense filter for the selected statuses, matching getRowConsentStatus.
export const getConsentStatusFilter = (statuses, now = Date.now()) => {
	const clauses = statuses.map((status) => {
		switch (status) {
			// None also covers rows with no status set; Typesense's != includes missing fields.
			case CONSENT_STATUS.NONE:
				return `informedConsentStatus:!=[${CONSENT_STATUS.RECEIVED},${CONSENT_STATUS.EXPIRED},${CONSENT_STATUS.NOT_APPLICABLE}]`;
			case CONSENT_STATUS.RECEIVED:
				return `(informedConsentStatus:${status} && informedConsentDateExpiry:>=${now})`;
			case CONSENT_STATUS.EXPIRED:
				return `(informedConsentStatus:${status} || (informedConsentStatus:${CONSENT_STATUS.RECEIVED} && informedConsentDateExpiry:<${now}))`;
			default:
				return `informedConsentStatus:${status}`;
		}
	});
	return `(${clauses.join(' || ')})`;
};

export const toMillis = (value) => safelyParseDate(value)?.getTime() ?? 0;

export const getConsentRecordTime = (record) =>
	toMillis(record?.dateReceived) || toMillis(record?.createdAt);

// Mirrors fieldsFromRecords in backend/functions/services/database/informedConsent: a client's
// consent fields come from its latest record, by dateReceived then createdAt.
export const getConsentFieldsFromRecords = (records, now = new Date()) => {
	if (records.length === 0) {
		return {
			informedConsentStatus: CONSENT_STATUS.NONE,
			informedConsentDate: null,
			informedConsentDateExpiry: null,
		};
	}

	const latest = records.reduce((current, record) =>
		getConsentRecordTime(record) >= getConsentRecordTime(current) ? record : current,
	);
	const dateReceived = safelyParseDate(latest.dateReceived);

	return {
		informedConsentStatus: getConsentStatus(dateReceived, now, latest.notApplicable),
		informedConsentDate:
			(dateReceived ?? safelyParseDate(latest.createdAt))?.toISOString() ?? null,
		informedConsentDateExpiry: null,
		...(!latest.notApplicable &&
			dateReceived && {
				informedConsentDateExpiry: addYears(
					dateReceived,
					INFORMED_CONSENT_VALIDITY_YEARS,
				).toISOString(),
			}),
	};
};

export const mapInformedConsentDoc = (docSnap) => {
	const data = docSnap.data() ?? {};
	const notApplicable = Boolean(data.notApplicable);
	const dateReceived = safelyParseDate(data.dateReceived)?.toISOString() ?? null;
	const createdAt = safelyParseDate(data.createdAt)?.toISOString() ?? null;
	const updatedAt = safelyParseDate(data.updatedAt)?.toISOString() ?? null;

	return {
		id: docSnap.id,
		informedConsentId: data.informedConsentId ?? docSnap.id,
		clientId: data.clientId,
		dealId: data.dealId ?? null,
		brokerGroupId: data.brokerGroupId,
		dateReceived,
		notes: data.notes ?? data.note ?? '',
		attachments: Array.isArray(data.attachments) ? data.attachments : [],
		recordedById: data.recordedById,
		recordedByName: data.recordedByName,
		notApplicable,
		createdAt,
		updatedAt,
		status: getConsentStatus(dateReceived, new Date(), notApplicable),
	};
};

export const mapUploadedAttachment = (file) => ({
	fileId: file?.id ?? file?.fileId ?? null,
	fileName: file?.name ?? file?.fileName ?? '',
	webUrl: file?.webUrl ?? file?.url ?? file?.downloadUrl ?? null,
	driveId: file?.parentReference?.driveId ?? file?.driveId ?? null,
	mimeType: file?.file?.mimeType ?? file?.mimeType ?? null,
});
